// PortalShell — top-level layout for every /portal/* route.
//
// Desktop (md+): logo + brand bar on top, vertical sidebar on the left,
// page content via <Outlet />. Same shape as the admin Layout so the
// admin and the franchisee feel like one family of products.
//
// Mobile/tablet (<md): the sidebar collapses into the existing
// PortalBottomNav (bottom-tab style) so franchisees on a phone keep
// the thumb-friendly nav they're used to.
//
// Each tab is a real route — no more accordion / single-page collapse.
// Tapping "Invoicing" loads /portal/invoices, tapping "Territory" loads
// /portal/territory, etc.
import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useHubUsage } from "@/lib/hubUsage";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import useUnreadUpdates from "@/hooks/useUnreadUpdates";
import {
  User as UserIcon, MapPin, CalendarDays, FolderOpen, Receipt,
  LogOut, Type, Loader2, AlertCircle, Megaphone, GraduationCap,
  CalendarClock, KeyRound, Sparkles, UserCog, ChevronDown, ShoppingBag,
  Menu, X, Facebook, LifeBuoy, Compass, Image as PosterIcon,
} from "lucide-react";
import PortalHelpModal from "@/components/portal/PortalHelpModal";
import PortalNewVersionBanner from "@/components/portal/PortalNewVersionBanner";
import HubPlusReminderBanner from "@/components/portal/HubPlusReminderBanner";
import SidebarVersionPill from "@/components/SidebarVersionPill";

// Private community Facebook group — every franchisee gets the same
// button to keep the link consistent and visible across every portal
// page. Meta blocks embedding private groups so the only viable UX
// is opening it in a new tab.
const COMFORT_ZONE_FB_URL = "https://www.facebook.com/groups/223912961485958/";

const FONT_SCALES = {
  small: { label: "Small", zoom: 0.9 },
  medium: { label: "Medium", zoom: 1 },
  large: { label: "Large", zoom: 1.15 },
  xlarge: { label: "Extra large", zoom: 1.3 },
};

// Map a tab id to a route. Sections are split with thin dividing rules
// per Paul's spec.
//
// Standard modules (always available): My Franchise, My Territory,
//   Calendar, HQ Updates, File Vault.
//
// Plus add-ons (subscription-gated, toggled per franchisee via the
// admin "Portal Modules" panel):
//   • territory_plus  → label becomes "My Territory+"
//   • marketing       → adds "Marketing" item
//   • invoicing       → adds "Invoicing" item
//
// Demo accounts get a single toggle ("Show all Hub+ pages") which the
// visitor can flip on or off — the demo's `portal_modules` flags are
// ignored in favour of the toggle so the experience is consistent for
// every person who lands on the demo URL.
function buildTabs({ modules, isDemo, demoShowPlus }) {
  const sections = [];
  // ---- Section 1: identity, territory, marketing, invoicing ----
  const s1 = [
    { to: "/portal/details", label: "My Franchise", icon: UserIcon, end: true, testid: "portal-nav-profile" },
  ];
  if (isDemo) {
    // Demo: render exactly one Territory row at a time. With the
    // toggle OFF we show the basic vanilla view; with it ON we show
    // the Territory+ upgrade and the other +bolt-ons.
    if (demoShowPlus) {
      s1.push({
        to: "/portal/territory",
        label: "My Territory+",
        icon: MapPin,
        end: true,
        testid: "portal-nav-territory-plus",
      });
      // Bookings+ hidden across all portals (Feb 2026, Sam) while the
      // module is unfinished. Backend/pricing/entitlement code retained
      // for when it ships — this is a nav-visibility change only.
      s1.push({ to: "/portal/marketing", label: "Marketing+", icon: Megaphone, testid: "portal-nav-marketing" });
      s1.push({ to: "/portal/invoices", label: "Invoicing+", icon: Receipt, testid: "portal-nav-invoices" });
    } else {
      s1.push({
        to: "/portal/territory/basic",
        label: "My Territory",
        icon: MapPin,
        end: true,
        testid: "portal-nav-territory",
      });
    }
  } else {
    if (modules.map !== false) {
      s1.push({
        to: "/portal/territory",
        label: modules.territory_plus ? "My Territory+" : "My Territory",
        icon: MapPin,
        testid: "portal-nav-territory",
      });
    }
    // Bookings+ hidden across all franchisee portals (Feb 2026, Sam)
    // while the module is unfinished. The /portal/bookings route stays
    // wired in App.js so bookmarked/deep links still resolve, but the
    // sidebar entry is removed. Restore this push when Bookings+ ships.
    if (modules.marketing === true) {
      s1.push({ to: "/portal/marketing", label: "Marketing+", icon: Megaphone, testid: "portal-nav-marketing" });
    }
    if (modules.invoicing === true) {
      s1.push({ to: "/portal/invoices", label: "Invoicing+", icon: Receipt, testid: "portal-nav-invoices" });
    }
  }
  sections.push(s1);
  // ---- Section 2: comms + scheduling ----
  const s2 = [];
  if (modules.calendar !== false) s2.push({ to: "/portal/events", label: "Calendar", icon: CalendarDays, testid: "portal-nav-events" });
  // Video Hub is available for ALL franchisees (not gated).
  s2.push({ to: "/portal/training", label: "Video Hub", icon: GraduationCap, testid: "portal-nav-training" });
  s2.push({ to: "/portal/updates", label: "HQ Updates", icon: Megaphone, testid: "portal-nav-updates" });
  sections.push(s2);
  // ---- Section 3: documents (File Vault) ----
  // (Contracts is now an inline section on the My Franchise page — no
  // separate sidebar tab. The /portal/contracts route still exists for
  // HQ email deep-links, but is not linked in nav.)
  const s3 = [];
  if (modules.files !== false) s3.push({ to: "/portal/files", label: "File Vault", icon: FolderOpen, testid: "portal-nav-files" });
  // Poster Studio — standard tool for every franchisee (not HUB+ gated).
  s3.push({ to: "/portal/posters", label: "Poster Studio", icon: PosterIcon, testid: "portal-nav-posters" });
  sections.push(s3);
  // ---- Section 4: HQ shop (Shape Orders) — sits in its own band
  // between File Vault and Account so it visually reads as a
  // "stores / supplies" area rather than a portal feature.
  const s4 = [];
  if (modules.shape_orders === true) {
    s4.push({ to: "/portal/shape-orders", label: "Franchise Store", icon: ShoppingBag, testid: "portal-nav-shape-orders" });
  }
  sections.push(s4);
  // ---- Section 5: account (always last) — change password, subscriptions, sign out
  const s5 = [
    { to: "/portal/account/password", label: "Change password", icon: KeyRound, testid: "portal-nav-password" },
    { to: "/portal/account/subscriptions", label: "Subscriptions", icon: Sparkles, testid: "portal-nav-subscriptions" },
  ];
  sections.push(s5);
  return sections.filter((s) => s.length);
}

export default function PortalShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/me");
        setProfile(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load your dashboard.");
      }
    })();
  }, []);

  // Discovery Portal URL — resolved by the backend from the reverse
  // link (contact.converted_to_franchisee_id ↔ franchisee.id). Only
  // set when a prospect-era Discovery Portal exists for this
  // franchisee; the nav row hides when it's absent. Same-origin
  // relative path (e.g. ``/discovery/sue-young``) so nothing is
  // hard-coded on the frontend.
  const discoveryPortalUrl = profile?.discovery_portal_url || null;

  const [fontScale, setFontScale] = useState(() => {
    try {
      const v = localStorage.getItem("portal.fontScale");
      return FONT_SCALES[v] ? v : "medium";
    } catch { return "medium"; }
  });
  useEffect(() => {
    try { localStorage.setItem("portal.fontScale", fontScale); }
    catch (e) { console.debug("[PortalShell] localStorage write blocked", e); }
  }, [fontScale]);

  const modules = profile?.profile?.portal_modules || {};
  useHubUsage({ user, ready: Boolean(profile?.profile), path: location.pathname, modules });
  // Demo accounts get extra side-by-side nav entries (e.g. "My Territory"
  // AND "My Territory+") so the user can show off the upgrade to
  // prospective franchisees.
  // Defensive: ``tags`` was expected to be an array but the backend
  // sometimes stores it as a comma-separated string on legacy
  // franchisee records (this whitescreened helen.bell@). Coerce.
  const rawTags = profile?.profile?.tags;
  const tags = Array.isArray(rawTags)
    ? rawTags
    : (typeof rawTags === "string" ? rawTags.split(/[,;]/) : []);
  const isDemo = tags.some((t) => String(t).trim().toLowerCase() === "demo");

  // Demo-only toggle: visitors land on the vanilla view; flipping
  // this on switches the sidebar to the Hub+ bolt-on view (and
  // swaps the basic Territory for Territory+). Persisted per-browser
  // in localStorage so the toggle survives across tabs / refreshes.
  const [demoShowPlus, setDemoShowPlus] = useState(() => {
    try { return localStorage.getItem("portal.demo.showPlus") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("portal.demo.showPlus", demoShowPlus ? "1" : "0"); }
    catch (e) { console.debug("[PortalShell] demo toggle persist failed", e); }
  }, [demoShowPlus]);

  const sections = buildTabs({ modules, isDemo, demoShowPlus });

  // HQ Updates unread badge — visible on the sidebar + bottom-nav.
  // Polls every 60s and on focus; refresh() is also called explicitly
  // by PortalUpdatesPage via the OutletContext below when the user
  // opens an update so the badge clears instantly.
  const { unread: unreadUpdates, refresh: refreshUnreadUpdates } = useUnreadUpdates(!!profile);

  // Account dropdown — auto-opens if the user is currently on any
  // /portal/account/* route (so a fresh navigate doesn't hide the
  // active tab) or if they explicitly toggled it open.
  const onAccountRoute = location.pathname.startsWith("/portal/account");
  const [accountOpen, setAccountOpen] = useState(onAccountRoute);
  useEffect(() => {
    if (onAccountRoute) setAccountOpen(true);
  }, [onAccountRoute]);

  // Mobile drawer state — closes automatically on route change so the
  // user lands on the new page without the menu still covering it.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  // Lock background scroll while drawer is open.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileNavOpen]);

  return (
    <div
      className="min-h-screen bg-[#FBFAF8] pl-safe pr-safe"
      style={{ zoom: FONT_SCALES[fontScale].zoom }}
      data-testid="portal-shell"
    >
      {/* Banner that appears when a new backend build is detected.
          Sits above the sticky header so it pushes the rest of the
          layout down a couple of lines until the franchisee
          refreshes or dismisses. */}
      <PortalNewVersionBanner />
      {/* +HUB complimentary window countdown — shows only when the
          franchisee is within 7 days of their complimentary window
          ending. Dismissible per-session; auto-re-shows after 24h.
          Hidden on the Subscriptions page itself — that page now
          renders a much larger, always-on complimentary-access
          countdown panel inline, so the global banner would just
          duplicate it. */}
      {location.pathname !== "/portal/account/subscriptions" && (
        <HubPlusReminderBanner />
      )}
      {/* Top bar — logo, font-size, sign-out (desktop). Always sticky. */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30 pt-safe">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2">
          <Logo className="h-12 sm:h-16 shrink-0 max-w-[55%]" />
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Help — opens a full-screen modal with the marked-up
                screenshot HQ has uploaded for the current page. Same
                style + position the Comfort Zone button used to live
                in; Comfort Zone has moved into the sidebar. */}
            <button
              onClick={() => setHelpOpen(true)}
              data-testid="portal-help-btn"
              title="Show me what this page does"
              className="px-2.5 sm:px-4 py-2 text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg flex items-center gap-1.5 sm:gap-2 transition-colors shadow-sm"
            >
              <LifeBuoy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Help</span>
            </button>
            <button
              onClick={() => {
                const order = ["small", "medium", "large", "xlarge"];
                const next = order[(order.indexOf(fontScale) + 1) % order.length];
                setFontScale(next);
              }}
              title={`Text size: ${FONT_SCALES[fontScale].label} — click for the next size`}
              data-testid="portal-font-size"
              className="px-2.5 sm:px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg flex items-center gap-1.5"
            >
              <Type className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Text · {FONT_SCALES[fontScale].label}</span>
              <span className="md:hidden">{FONT_SCALES[fontScale].label}</span>
            </button>
            <div className="text-right hidden lg:block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Signed in as</div>
              <div className="text-xs text-stone-900 font-mono truncate max-w-[180px]">{user?.email}</div>
            </div>
            {/* Hamburger — mobile only. Opens the full-screen drawer that
                mirrors the desktop sidebar. */}
            <button
              onClick={() => setMobileNavOpen(true)}
              data-testid="portal-mobile-menu-btn"
              aria-label="Open menu"
              className="md:hidden p-2 -mr-1 text-stone-800 hover:bg-stone-100 rounded-lg"
            >
              <Menu className="w-6 h-6" strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </header>

      {err && (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 mt-4">
          <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {err}
          </div>
        </div>
      )}
      {!profile && !err && (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 mt-4">
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your account…
          </div>
        </div>
      )}

      {profile && (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-5 sm:py-8 flex gap-6">
          {/* Sidebar — md+ only. Mobile uses PortalBottomNav (rendered
              below outside the flex container). */}
          <aside className="hidden md:block w-56 shrink-0" data-testid="portal-sidebar">
            <nav className="sticky top-24 space-y-1">
              {sections.map((tabs, sIdx) => {
                const isAccount = sIdx === sections.length - 1;
                // Section identity = the first tab's path (each section has
                // a unique opening route). Falls back to "account" for the
                // dropdown section which lives at the end.
                const sectionKey = isAccount ? "account" : (tabs[0]?.to || `section-${sIdx}`);
                if (isAccount) {
                  return (
                    <div key={sectionKey}>
                      <div className="my-3 border-t border-stone-200" data-testid={`portal-nav-divider-${sIdx}`} />
                      {/* Discovery Portal — the prospect-era portal
                          the franchisee saw during their sales
                          journey. Only rendered when the backend
                          reverse-lookup resolved a slug for this
                          franchisee (contact.converted_to_franchisee_id
                          ↔ franchisee.id). Opens same-tab so cookies
                          set on the same origin carry through. */}
                      {discoveryPortalUrl && (
                        <a
                          href={discoveryPortalUrl}
                          data-testid="portal-nav-discovery-portal"
                          title="Open your Discovery Portal"
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg font-medium transition-colors text-stone-700 hover:bg-stone-100"
                        >
                          <Compass className="w-4 h-4 shrink-0 text-stone-700" />
                          <span>Discovery Portal</span>
                        </a>
                      )}
                      {/* Creative Mojo Comfort Zone — Facebook group
                          link sitting between Franchise Store and the
                          Account dropdown, framed by the regular
                          section dividers so it reads as a peer of the
                          other nav rows rather than a special CTA. */}
                      <a
                        href={COMFORT_ZONE_FB_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="portal-nav-comfort-zone"
                        title="Open the Creative Mojo Comfort Zone private Facebook group"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg font-medium transition-colors text-stone-700 hover:bg-stone-100"
                      >
                        <Facebook className="w-4 h-4 shrink-0 text-[#1877F2]" />
                        <span>Comfort Zone</span>
                      </a>
                      <div className="my-3 border-t border-stone-200" data-testid={`portal-nav-divider-${sIdx}-account`} />
                      <button
                        type="button"
                        onClick={() => setAccountOpen((v) => !v)}
                        aria-expanded={accountOpen}
                        data-testid="portal-nav-account-toggle"
                        className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm rounded-lg font-medium transition-colors ${
                          accountOpen ? "bg-stone-100 text-stone-950" : "text-stone-700 hover:bg-stone-100"
                        }`}
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <UserCog className="w-4 h-4 shrink-0" />
                          <span>Account</span>
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 shrink-0 transition-transform ${accountOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {accountOpen && (
                        <div className="mt-1 ml-3 pl-3 border-l border-stone-200 space-y-1" data-testid="portal-nav-account-panel">
                          {tabs.map(({ to, label, icon: Icon, end, testid }) => (
                            <NavLink
                              key={to}
                              to={to}
                              end={end}
                              data-testid={testid}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                                  isActive
                                    ? "bg-stone-950 text-white"
                                    : "text-stone-700 hover:bg-stone-100"
                                }`
                              }
                            >
                              <Icon className="w-4 h-4" />
                              {label}
                            </NavLink>
                          ))}
                          <button
                            onClick={logout}
                            data-testid="portal-logout"
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg font-medium transition-colors text-stone-700 hover:bg-stone-100"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign out
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div key={sectionKey}>
                    {sIdx > 0 && <div className="my-3 border-t border-stone-200" data-testid={`portal-nav-divider-${sIdx}`} />}
                    {tabs.map(({ to, label, icon: Icon, end, testid }) => {
                      const showBadge = to === "/portal/updates" && unreadUpdates > 0;
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          end={end}
                          data-testid={testid}
                          className={({ isActive }) =>
                            `relative flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg font-medium transition-colors ${
                              isActive
                                ? "bg-stone-950 text-white"
                                : "text-stone-700 hover:bg-stone-100"
                            }`
                          }
                        >
                          <Icon className="w-4 h-4" />
                          <span className="flex-1">{label}</span>
                          {showBadge && (
                            <span
                              data-testid="nav-updates-badge"
                              className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold rounded-full bg-rose-600 text-white tabular-nums shadow-sm"
                              title={`${unreadUpdates} unread update${unreadUpdates === 1 ? "" : "s"}`}
                            >
                              {unreadUpdates > 99 ? "99+" : unreadUpdates}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                );
              })}
              {/* Demo-only toggle — pinned at the very bottom of the
                  sidebar so it sits outside the real-page nav rows.
                  Off by default; flipping it on swaps the standard
                  pages for the Hub+ bolt-on view (Territory+,
                  Marketing+, Invoicing+) so prospective
                  franchisees visiting the demo URL can preview the
                  upgrade. */}
              {isDemo && (
                <div className="mt-6 pt-4 border-t-2 border-stone-200">
                  <button
                    type="button"
                    onClick={() => setDemoShowPlus((v) => !v)}
                    data-testid="portal-nav-demo-show-plus"
                    aria-pressed={demoShowPlus}
                    title={demoShowPlus
                      ? "Currently showing the Hub+ bolt-on pages. Click to switch back to the standard pages."
                      : "Click to reveal the Hub+ bolt-on pages (Territory+, Marketing+, Invoicing+)."}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm rounded-lg font-medium border transition-colors ${
                      demoShowPlus
                        ? "bg-stone-950 border-stone-950 text-[#dddd16] hover:bg-stone-800"
                        : "bg-[#dddd16]/15 border-[#dddd16]/60 text-stone-900 hover:bg-[#dddd16]/30"
                    }`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <Sparkles className="w-4 h-4 shrink-0" />
                      <span className="truncate">
                        {demoShowPlus ? "Hide Hub+ pages" : "Show all Hub+ pages"}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 inline-flex items-center w-9 h-5 rounded-full transition-colors ${
                        demoShowPlus ? "bg-[#dddd16] justify-end" : "bg-stone-300 justify-start"
                      }`}
                      aria-hidden="true"
                    >
                      <span className="w-4 h-4 mx-0.5 bg-white rounded-full shadow-sm" />
                    </span>
                  </button>
                  <p className="mt-2 px-1 text-[10px] uppercase tracking-wider font-bold text-stone-500" data-testid="portal-nav-demo-show-plus-hint">
                    Demo preview toggle
                  </p>
                </div>
              )}
            </nav>
            <SidebarVersionPill />
          </aside>

          {/* Main content — page-specific via Outlet. */}
          <main className="flex-1 min-w-0 pb-8" data-testid="portal-main">
            {/* Provide the profile + unread-updates refresher to children via outlet context */}
            <Outlet context={{
              profile,
              refreshProfile: async () => {
                const { data } = await api.get("/portal/me");
                setProfile(data);
              },
              refreshUnreadUpdates,
            }} />
          </main>
        </div>
      )}

      {/* Mobile drawer — slides down from the top, mirrors the desktop
          sidebar order including the Account dropdown. Visible <md only. */}
      <PortalMobileDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        sections={sections}
        accountOpen={accountOpen}
        onToggleAccount={() => setAccountOpen((v) => !v)}
        onLogout={logout}
        userEmail={user?.email}
        unreadUpdates={unreadUpdates}
        isDemo={isDemo}
        demoShowPlus={demoShowPlus}
        onToggleDemoShowPlus={() => setDemoShowPlus((v) => !v)}
        discoveryPortalUrl={discoveryPortalUrl}
      />
      <PortalHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

// Mobile drawer — full-screen overlay that mirrors the desktop sidebar.
// Last section is the Account dropdown so behaviour matches md+ exactly.
function PortalMobileDrawer({
  open, onClose, sections, accountOpen, onToggleAccount, onLogout, userEmail,
  unreadUpdates = 0,
  isDemo = false, demoShowPlus = false, onToggleDemoShowPlus = null,
  discoveryPortalUrl = null,
}) {
  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-50" data-testid="portal-mobile-drawer">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-stone-950/60 backdrop-blur-sm"
        data-testid="portal-mobile-drawer-backdrop"
      />
      {/* Panel — slides in from the right, full height */}
      <div
        className="absolute top-0 right-0 bottom-0 w-[86%] max-w-sm bg-white shadow-2xl flex flex-col pt-safe pb-safe animate-[slideIn_180ms_ease-out]"
        style={{ animationName: "slideIn" }}
      >
        <div className="px-5 py-4 flex items-center justify-between border-b border-stone-200">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Signed in as</div>
            <div className="text-xs text-stone-900 font-mono truncate">{userEmail}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="portal-mobile-drawer-close"
            aria-label="Close menu"
            className="p-2 -mr-1 text-stone-800 hover:bg-stone-100 rounded-lg shrink-0"
          >
            <X className="w-6 h-6" strokeWidth={2.2} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {sections.map((tabs, sIdx) => {
            const isAccount = sIdx === sections.length - 1;
            const sectionKey = isAccount ? "account" : (tabs[0]?.to || `section-${sIdx}`);
            if (isAccount) {
              return (
                <div key={sectionKey}>
                  <div className="my-3 border-t border-stone-200" />
                  {discoveryPortalUrl && (
                    <a
                      href={discoveryPortalUrl}
                      onClick={onClose}
                      data-testid="portal-mobile-discovery-portal"
                      title="Open your Discovery Portal"
                      className="w-full flex items-center gap-3 px-3 py-3 text-base rounded-lg font-medium transition-colors text-stone-700 hover:bg-stone-100"
                    >
                      <Compass className="w-5 h-5 shrink-0 text-stone-700" />
                      <span>Discovery Portal</span>
                    </a>
                  )}
                  <a
                    href={COMFORT_ZONE_FB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    data-testid="portal-mobile-comfort-zone"
                    className="w-full flex items-center gap-3 px-3 py-3 text-base rounded-lg font-medium transition-colors text-stone-700 hover:bg-stone-100"
                  >
                    <Facebook className="w-5 h-5 shrink-0 text-[#1877F2]" />
                    <span>Comfort Zone</span>
                  </a>
                  <div className="my-3 border-t border-stone-200" />
                  <button
                    type="button"
                    onClick={onToggleAccount}
                    aria-expanded={accountOpen}
                    data-testid="portal-mobile-account-toggle"
                    className={`w-full flex items-center justify-between gap-3 px-3 py-3 text-base rounded-lg font-medium transition-colors ${
                      accountOpen ? "bg-stone-100 text-stone-950" : "text-stone-700 hover:bg-stone-100"
                    }`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <UserCog className="w-5 h-5 shrink-0" />
                      <span>Account</span>
                    </span>
                    <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${accountOpen ? "rotate-180" : ""}`} />
                  </button>
                  {accountOpen && (
                    <div className="mt-1 ml-3 pl-3 border-l border-stone-200 space-y-1">
                      {tabs.map(({ to, label, icon: Icon, end, testid }) => (
                        <NavLink
                          key={to}
                          to={to}
                          end={end}
                          data-testid={testid}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 text-base rounded-lg font-medium transition-colors ${
                              isActive
                                ? "bg-stone-950 text-white"
                                : "text-stone-700 hover:bg-stone-100"
                            }`
                          }
                        >
                          <Icon className="w-5 h-5" />
                          {label}
                        </NavLink>
                      ))}
                      <button
                        onClick={onLogout}
                        data-testid="portal-mobile-logout"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-base rounded-lg font-medium transition-colors text-stone-700 hover:bg-stone-100"
                      >
                        <LogOut className="w-5 h-5" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={sectionKey}>
                {sIdx > 0 && <div className="my-3 border-t border-stone-200" />}
                {tabs.map(({ to, label, icon: Icon, end, testid }) => {
                  const showBadge = to === "/portal/updates" && unreadUpdates > 0;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      data-testid={testid}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-3 py-3 text-base rounded-lg font-medium transition-colors ${
                          isActive
                            ? "bg-stone-950 text-white"
                            : "text-stone-700 hover:bg-stone-100"
                        }`
                      }
                    >
                      <Icon className="w-5 h-5" />
                      <span className="flex-1">{label}</span>
                      {showBadge && (
                        <span
                          data-testid="mobile-nav-updates-badge"
                          className="inline-flex items-center justify-center min-w-[1.4rem] h-6 px-1.5 text-xs font-bold rounded-full bg-rose-600 text-white tabular-nums shadow-sm"
                        >
                          {unreadUpdates > 99 ? "99+" : unreadUpdates}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
          {/* Demo-only toggle pinned at the bottom of the drawer — same
              behaviour and styling as the desktop sidebar version. */}
          {isDemo && onToggleDemoShowPlus && (
            <div className="mt-6 pt-4 border-t-2 border-stone-200">
              <button
                type="button"
                onClick={onToggleDemoShowPlus}
                aria-pressed={demoShowPlus}
                data-testid="portal-mobile-demo-show-plus"
                className={`w-full flex items-center justify-between gap-3 px-3 py-3 text-base rounded-lg font-medium border transition-colors ${
                  demoShowPlus
                    ? "bg-stone-950 border-stone-950 text-[#dddd16] hover:bg-stone-800"
                    : "bg-[#dddd16]/15 border-[#dddd16]/60 text-stone-900 hover:bg-[#dddd16]/30"
                }`}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <Sparkles className="w-5 h-5 shrink-0" />
                  <span>{demoShowPlus ? "Hide Hub+ pages" : "Show all Hub+ pages"}</span>
                </span>
                <span
                  className={`shrink-0 inline-flex items-center w-10 h-6 rounded-full transition-colors ${
                    demoShowPlus ? "bg-[#dddd16] justify-end" : "bg-stone-300 justify-start"
                  }`}
                  aria-hidden="true"
                >
                  <span className="w-5 h-5 mx-0.5 bg-white rounded-full shadow-sm" />
                </span>
              </button>
              <p className="mt-2 px-1 text-[10px] uppercase tracking-wider font-bold text-stone-500">
                Demo preview toggle
              </p>
            </div>
          )}
        </nav>
      </div>

      {/* Inline keyframes for the slide-in panel (Tailwind doesn't ship
          this out of the box and adding it to tailwind.config is overkill
          for a single transition). */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
